import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, switchMap, tap } from 'rxjs/operators';
import { SampleCensorSign, SampleCensorType } from 'src/app/enums/sampleCensorType.enum';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { SampleOutlierService } from 'src/app/services/sample-outlier.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { isExcludedFromStatistics } from 'src/app/shared/utils/distribution-label.util';

export interface Sample {
  sampleId: string;
  id?: string;
  surveyName: string;
  sampleNumber: string;
  startDate: Date;
  value: number;
  unit: string;
  isOutlier?: boolean;
  outlierReason?: string | null;
  displayValue?: string;
  sampleCensorType?: number;
  substitutedValue?: number | null;
  substitutedDisplay?: string;
  isExcluded?: boolean;
}

@Component({
  standalone: false,
  selector: 'app-sample-grid-view-dialog',
  templateUrl: './sample-grid-view-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sample-grid-view-dialog.component.scss']
})
export class SampleGridViewDialogComponent implements OnDestroy {
  samples: Sample[] = [];
  activeSamples: Sample[] = [];
  outlierSamples: Sample[] = [];
  dateFormat: string;
  riskRegisterId: string;
  readOnly = false;
  isLoading = false;
  savingOutlierId: string | null = null;
  outlierChanged = false;
  showOutlierSection = false;

  private destroy$ = new Subject<void>();

  public trackByIndex = trackByIndex;

  constructor(
    public dialogRef: MatDialogRef<SampleGridViewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { id: string, riskAssessmentId: string, riskRegisterSamples: Sample[], readOnly?: boolean },
    private dateFormatService: DateFormatService,
    private sampleOutlierService: SampleOutlierService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.dateFormat = dateFormatService.userDateWithTimeFormat;
    this.riskRegisterId = data.id;
    this.readOnly = data.readOnly ?? false;
    this.samples = data.riskRegisterSamples.map((result) => {
      let displayValue = '';
      displayValue += result?.sampleCensorType != null && result.sampleCensorType !== SampleCensorType.NA
        ? SampleCensorSign[result.sampleCensorType] : '';
      displayValue += `${result.value}  ${result.unit}`;
      const isExcluded = isExcludedFromStatistics(result.sampleCensorType, result.substitutedValue);
      return {
        displayValue: displayValue,
        substitutedDisplay: this.buildSubstitutedDisplay(result, isExcluded),
        isExcluded: isExcluded,
        ...result,
      };
    });
    this.refreshSampleLists();
  }

  // The value the sample actually contributed to the statistics, in the display
  // unit (already converted upstream). Excluded non-detects have no substituted
  // value, so they are flagged rather than showing a number.
  private buildSubstitutedDisplay(sample: Sample, isExcluded: boolean): string {
    if (isExcluded) {
      return this.translateService.getDynamicTranslation('Excluded from statistics');
    }
    return sample.substitutedValue != null
      ? `${sample.substitutedValue}${sample.unit ? ` ${sample.unit}` : ''}`
      : '-';
  }

  private refreshSampleLists(): void {
    this.activeSamples = this.samples.filter(s => !s.isOutlier);
    this.outlierSamples = this.samples.filter(s => s.isOutlier);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get outlierCount(): number {
    return this.outlierSamples.length;
  }

  markAsOutlier(sample: Sample) {
    if (this.readOnly) {
      return;
    }
    const sampleId = this.getValidSampleId(sample);
    if (sampleId == null) {
      const errorMsg = this.translateService.getDynamicTranslation('Unable to update outlier status: missing sample id');
      this.toastService.displayErrorMessage(errorMsg, true);
      return;
    }
    this.sampleOutlierService.promptOutlierReason(sample.outlierReason).pipe(
      switchMap((reason: string) => {
        this.savingOutlierId = sampleId;
        return this.sampleOutlierService.setOutlier(sampleId, { isOutlier: true, outlierReason: reason }).pipe(
          tap(() => {
            sample.isOutlier = true;
            sample.outlierReason = reason;
            this.refreshSampleLists();
          }),
          takeUntil(this.destroy$)
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        const successMessage = this.translateService.getDynamicTranslation('Sample marked as outlier');
        this.toastService.showSuccess(successMessage, true);
        this.triggerRecalculate();
      },
      error: () => {
        this.savingOutlierId = null;
        const errorMessage = this.translateService.getDynamicTranslation('Failed to mark sample as outlier');
        this.toastService.displayErrorMessage(errorMessage, true);
      }
    });
  }

  unmarkAsOutlier(sample: Sample) {
    if (this.readOnly) {
      return;
    }
    const sampleId = this.getValidSampleId(sample);
    if (sampleId == null) {
      const errorMsg = this.translateService.getDynamicTranslation('Unable to update outlier status: missing sample id');
      this.toastService.displayErrorMessage(errorMsg, true);
      return;
    }
    this.savingOutlierId = sampleId;

    this.sampleOutlierService.setOutlier(sampleId, { isOutlier: false, outlierReason: null }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        sample.isOutlier = false;
        sample.outlierReason = null;
        this.refreshSampleLists();
        const successMessage = this.translateService.getDynamicTranslation('Sample unmarked as outlier');
        this.toastService.showSuccess(successMessage, true);
        this.triggerRecalculate();
      },
      error: () => {
        this.savingOutlierId = null;
        const errorMessage = this.translateService.getDynamicTranslation('Failed to unmark sample as outlier');
        this.toastService.displayErrorMessage(errorMessage, true);
      }
    });
  }

  private getValidSampleId(sample: Sample): string | null {
    return sample?.sampleId ? sample.sampleId : null;
  }

  private triggerRecalculate() {
    this.isLoading = true;
    this.sampleOutlierService.recalculateRegister(this.riskRegisterId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.savingOutlierId = null;
        this.isLoading = false;
        this.outlierChanged = true;
        this.dialogRef.close(true);
      },
      error: () => {
        this.savingOutlierId = null;
        this.isLoading = false;
        const errorMessage = this.translateService.getDynamicTranslation('The sample was updated but the statistics could not be recalculated. The register is now out of date — use Recalculate to refresh it.');
        this.toastService.displayErrorMessage(errorMessage, true);
      }
    });
  }
}
